import { translationKey } from '../i18n';
import { localize, readAppLanguage } from '../i18n';
import SingleLineText from './ui/SingleLineText';
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import LightModeIcon from '@mui/icons-material/LightMode';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import BedtimeIcon from '@mui/icons-material/Bedtime';

import { FONT_FAMILY as FONT_STACK } from '../design/tokens';

const PERIODS = [
    { key: '30', label: translationKey('30일'), days: 29 },
    { key: '90', label: translationKey('90일'), days: 89 },
    { key: 'year', label: translationKey('올해'), days: null },
];

const WEEKDAY_LABELS = [translationKey('월'), translationKey('화'), translationKey('수'), translationKey('목'), translationKey('금'), translationKey('토'), translationKey('일')];
const TIME_SLOTS = [
    { key: 'MORNING', label: translationKey('아침'), Icon: WbSunnyIcon, color: '#f59e0b' },     // 노란 해
    { key: 'LUNCH', label: translationKey('점심'), Icon: LightModeIcon, color: '#eab308' },     // 정오 노란 해
    { key: 'EVENING', label: translationKey('저녁'), Icon: WbTwilightIcon, color: '#f97316' },  // 석양 주황
    { key: 'NIGHT', label: translationKey('밤'), Icon: BedtimeIcon, color: '#6366f1' },         // 달 보라
];

function formatLastTakenSince(lastTakenAt) {
    if (!lastTakenAt) return localize("기록 없음");
    const then = new Date(lastTakenAt);
    const diffMs = Date.now() - then.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return localize("방금 전");
    if (diffMin < 60) return localize("{v0}분 전", { v0: diffMin });
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return localize("{v0}시간 전", { v0: diffH });
    const diffD = Math.floor(diffH / 24);
    if (diffD < 30) return localize("{v0}일 전", { v0: diffD });
    const diffMo = Math.floor(diffD / 30);
    if (diffMo < 12) return localize("{v0}달 전", { v0: diffMo });
    return localize("{v0}년 전", { v0: Math.floor(diffMo / 12) });
}

function pad2(n) { return String(n).padStart(2, '0'); }

function formatRecentLogTime(takenAt) {
    const d = new Date(takenAt);
    const now = new Date();
    const sameYear = d.getFullYear() === now.getFullYear();
    const sameDay = d.toDateString() === now.toDateString();
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const timePart = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    if (sameDay) return localize("오늘 {v0}", { v0: timePart });
    if (isYesterday) return localize("어제 {v0}", { v0: timePart });
    if (sameYear) return localize("{v0}월 {v1}일 {v2}", { v0: d.getMonth() + 1, v1: d.getDate(), v2: timePart });
    return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${timePart}`;
}

/**
 * PRN 약 카드 펼침 영역: 기간 토글 + KPI + 막대 그래프 + 최근 기록.
 *
 * Props:
 * - stat: AsNeededMedicationStat — getStats 응답의 asNeeded 항목 1개
 * - period: '30' | '90' | 'year' — 외부 상태
 * - onPeriodChange(key)
 * - onEditLog(log)
 * - onDeleteLog(log)
 */
export default function PrnStatsDetail({ stat, period, onPeriodChange, onEditLog, onDeleteLog }) {
    const [showAllRecent, setShowAllRecent] = useState(false);
    // period 변경 후 새 stats fetch 중일 때 — 카드 펼침 영역에 잠깐 로딩 표시
    if (!stat) {
        return (
            <Box sx={{ py: '32px', textAlign: 'center', fontFamily: FONT_STACK }}>
                <SingleLineText sx={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>{localize("불러오는 중...")}</SingleLineText>
            </Box>
        );
    }

    const periodCount = stat.periodTakenCount || 0;
    const prevCount = stat.previousPeriodCount || 0;
    const trend = periodCount - prevCount;
    const trendIcon = trend > 0 ? '↑' : trend < 0 ? '↓' : '—';
    const trendColor = trend > 0 ? '#dc2626' : trend < 0 ? '#16a34a' : '#9CA3AF';
    const trendText = trend === 0 ? localize('동일') : `${trend > 0 ? '+' : ''}${trend}`;

    const sinceText = formatLastTakenSince(stat.lastTakenAt);

    const avgIntervalText = stat.avgIntervalDays != null
        ? (stat.avgIntervalDays >= 1
            ? localize("{v0}일", { v0: stat.avgIntervalDays.toFixed(1) })
            : localize("하루 {v0}회", { v0: (1 / stat.avgIntervalDays).toFixed(1) }))
        : '—';

    const longestGapText = stat.longestGapDays != null ? localize("{v0}일", { v0: stat.longestGapDays }) : '—';

    // 월별 차트
    const monthlyEntries = Object.entries(stat.monthlyCounts || {}).sort(([a], [b]) => a.localeCompare(b));
    const maxMonthly = Math.max(1, ...monthlyEntries.map(([, v]) => v));

    // 요일별 차트 — 항상 7개 보장 (백엔드 응답이 비어도 0으로 채움)
    const weekdayRaw = stat.weekdayDistribution || [];
    const weekdayDist = Array.from({ length: 7 }, (_, i) => weekdayRaw[i] ?? 0);
    const weekdaySum = weekdayDist.reduce((s, v) => s + v, 0);
    const maxWeekday = Math.max(1, ...weekdayDist);
    const topWeekdayIdx = weekdayDist.reduce((bestIdx, v, i, arr) => v > arr[bestIdx] ? i : bestIdx, 0);

    // 시간대별 차트 — 4개 슬롯 항상 보장
    const timeDist = stat.timeOfDayDistribution || {};
    const timeSum = TIME_SLOTS.reduce((s, slot) => s + (timeDist[slot.key] || 0), 0);
    const topTimeKey = Object.entries(timeDist).reduce((best, [k, v]) => v > (timeDist[best] || 0) ? k : best, 'MORNING');

    const recentLogs = stat.recentLogs || [];
    // useMemo를 if (!stat) return 뒤에 두지 않는다 — Hook 호출 개수가 렌더마다
    // 달라져 "Rendered more hooks than during the previous render" 에러 발생.
    const visibleRecent = showAllRecent ? recentLogs : recentLogs.slice(0, 5);

    return (
        <Box sx={{ pt: 1, fontFamily: FONT_STACK }}>
            {/* 기간 토글 */}
            <Box sx={{
                display: 'flex', gap: '6px', mb: '12px',
                bgcolor: '#f5f7fa', borderRadius: 999, p: '4px',
            }}>
                {PERIODS.map(p => (
                    <Box
                        key={p.key}
                        component="button"
                        type="button"
                        onClick={() => onPeriodChange(p.key)}
                        sx={{
                            flex: 1, border: 'none', cursor: 'pointer',
                            bgcolor: period === p.key ? '#fff' : 'transparent',
                            color: period === p.key ? '#0c4a6e' : '#6B7280',
                            fontSize: '0.85rem', fontWeight: 700, fontFamily: FONT_STACK,
                            py: '8px', borderRadius: 999,
                            boxShadow: period === p.key ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
                            '&:active': { transform: 'scale(0.97)' },
                        }}
                    >{localize(p.label)}</Box>
                ))}
            </Box>

            {/* KPI 2x2 그리드 */}
            <Box sx={{
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', mb: '14px',
            }}>
                <KpiCard
                    label={localize("이 기간 복용")}
                    value={localize("{v0}회", { v0: periodCount })}
                    sub={<Box component="span" sx={{ color: trendColor, fontWeight: 700 }}>
                        {trendIcon} {trendText}
                    </Box>}
                />
                <KpiCard label={localize("마지막 복용")} value={sinceText} />
                <KpiCard label={localize("평균 간격")} value={avgIntervalText} />
                <KpiCard label={localize("최장 무복용")} value={longestGapText} />
            </Box>

            {/* 월별 — 최근 6개월 */}
            {monthlyEntries.length > 0 && (
                <Box sx={{ mb: '14px' }}>
                    <SingleLineText sx={{ fontSize: 13, fontWeight: 700, color: '#1F2937', mb: '8px' }}>{localize("월별 복용")}</SingleLineText>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${monthlyEntries.length}, 1fr)`,
                        gap: '6px',
                        alignItems: 'end',
                        minHeight: 90,
                    }}>
                        {monthlyEntries.map(([month, count]) => {
                            const heightPct = (count / maxMonthly) * 100;
                            const isMax = count === maxMonthly && count > 0;
                            const monthLabel = localize('{v0}월', { v0: month.split('-')[1].replace(/^0/, '') });
                            return (
                                <Box key={month} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography sx={{
                                        fontSize: 11, fontWeight: 700,
                                        color: count > 0 ? '#0c4a6e' : '#9CA3AF',
                                        mb: '4px',
                                    }}>{count}</Typography>
                                    <Box sx={{
                                        width: '100%',
                                        height: count > 0 ? `${Math.max(heightPct, 12)}%` : '4px',
                                        minHeight: count > 0 ? 8 : 4,
                                        maxHeight: 60,
                                        bgcolor: count === 0 ? '#F3F4F6' : (isMax ? '#38bdf8' : '#bae6fd'),
                                        borderRadius: '6px 6px 0 0',
                                    }} />
                                    <Typography sx={{ fontSize: 11, color: '#6B7280', mt: '4px' }}>
                                        {monthLabel}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* 요일별 — 데이터 1건이라도 있으면 항상 7개 막대 노출 */}
            {weekdaySum > 0 && (
                <Box sx={{ mb: '14px' }}>
                    <SingleLineText sx={{ fontSize: 13, fontWeight: 700, color: '#1F2937', mb: '8px' }}>{localize("요일별 복용")}</SingleLineText>
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px', alignItems: 'end', minHeight: 70,
                    }}>
                        {weekdayDist.map((count, i) => {
                            const heightPct = (count / maxWeekday) * 100;
                            const isTop = i === topWeekdayIdx && count > 0;
                            return (
                                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography sx={{
                                        fontSize: 11, fontWeight: 700,
                                        color: count > 0 ? '#0c4a6e' : '#9CA3AF', mb: '4px',
                                    }}>{count}</Typography>
                                    <Box sx={{
                                        width: '100%',
                                        height: count > 0 ? `${Math.max(heightPct, 12)}%` : '4px',
                                        minHeight: count > 0 ? 8 : 4,
                                        maxHeight: 44,
                                        bgcolor: count === 0 ? '#F3F4F6' : (isTop ? '#38bdf8' : '#bae6fd'),
                                        borderRadius: '4px 4px 0 0',
                                    }} />
                                    <Typography sx={{
                                        fontSize: 11, color: isTop ? '#0c4a6e' : '#6B7280',
                                        fontWeight: isTop ? 700 : 500, mt: '3px',
                                    }}>{localize(WEEKDAY_LABELS[i])}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* 시간대별 — MUI 아이콘으로 통일 (이모지 X) */}
            {timeSum > 0 && (
                <Box sx={{ mb: '14px' }}>
                    <SingleLineText sx={{ fontSize: 13, fontWeight: 700, color: '#1F2937', mb: '8px' }}>{localize("시간대별 복용")}</SingleLineText>
                    <Box sx={{ display: 'flex', gap: '6px' }}>
                        {TIME_SLOTS.map(slot => {
                            const count = timeDist[slot.key] || 0;
                            const isTop = slot.key === topTimeKey && count > 0;
                            const Icon = slot.Icon;
                            return (
                                <Box key={slot.key} sx={{
                                    flex: 1, p: '8px 6px',
                                    bgcolor: isTop ? '#e0f2fe' : '#F9FAFB',
                                    borderRadius: '10px',
                                    border: isTop ? '1px solid #bae6fd' : '1px solid transparent',
                                    textAlign: 'center',
                                }}>
                                    <Icon sx={{
                                        fontSize: 18,
                                        color: count > 0 ? slot.color : '#cbd5e1',
                                    }} />
                                    <SingleLineText sx={{
                                        fontSize: 11, color: isTop ? '#0c4a6e' : '#6B7280',
                                        fontWeight: isTop ? 700 : 500, mt: '2px',
                                    }}>{localize(slot.label)}</SingleLineText>
                                    <Typography sx={{
                                        fontSize: 13, fontWeight: 800,
                                        color: count > 0 ? '#0c4a6e' : '#9CA3AF', mt: '2px',
                                    }}>{count}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* 최근 기록 */}
            {recentLogs.length > 0 && (
                <Box sx={{ mb: '8px' }}>
                    <SingleLineText sx={{ fontSize: 13, fontWeight: 700, color: '#1F2937', mb: '8px' }}>{localize("최근 기록")}</SingleLineText>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {visibleRecent.map(log => (
                            <Box key={log.logId} sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                py: '10px', px: '12px',
                                bgcolor: '#fff', borderRadius: '10px',
                                border: '1px solid #F3F4F6',
                                minHeight: 48,
                            }}>
                                <Typography sx={{
                                    fontSize: 13, fontWeight: 600, color: '#1F2937',
                                    fontVariantNumeric: 'tabular-nums',
                                }}>
                                    {formatRecentLogTime(log.takenAt)}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: '6px' }}>
                                    {onEditLog && (
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); onEditLog(log); }}
                                            sx={{
                                                border: '1px solid #bae6fd', bgcolor: '#f0f9ff',
                                                color: '#0369a1', fontSize: 12, fontWeight: 700,
                                                fontFamily: FONT_STACK,
                                                py: '6px', px: '12px', borderRadius: 999,
                                                cursor: 'pointer', minHeight: 32,
                                                '&:active': { transform: 'scale(0.96)' },
                                            }}
                                        >{localize("수정")}</Box>
                                    )}
                                    {onDeleteLog && (
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); onDeleteLog(log); }}
                                            sx={{
                                                border: '1px solid #fecaca', bgcolor: '#fef2f2',
                                                color: '#dc2626', fontSize: 12, fontWeight: 700,
                                                fontFamily: FONT_STACK,
                                                py: '6px', px: '12px', borderRadius: 999,
                                                cursor: 'pointer', minHeight: 32,
                                                '&:active': { transform: 'scale(0.96)' },
                                            }}
                                        >{localize("삭제")}</Box>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    {recentLogs.length > 5 && (
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setShowAllRecent(v => !v)}
                            sx={{
                                width: '100%', mt: '6px',
                                border: 'none', background: 'transparent',
                                color: '#0369a1', fontSize: 12, fontWeight: 700,
                                fontFamily: FONT_STACK,
                                py: '8px', cursor: 'pointer',
                                '&:active': { opacity: 0.6 },
                            }}
                        >
                            {showAllRecent ? localize("접기") : localize("{v0}개 더 보기", { v0: recentLogs.length - 5 })}
                        </Box>
                    )}
                </Box>
            )}

            {periodCount === 0 && (
                <Box sx={{
                    py: '24px', textAlign: 'center',
                    bgcolor: '#F9FAFB', borderRadius: '12px',
                }}>
                    <SingleLineText sx={{ fontSize: 13, color: '#6B7280', fontWeight: 600 }}>{localize("이 기간엔 기록이 없어요")}</SingleLineText>
                </Box>
            )}
        </Box>
    );
}

function KpiCard({ label, value, sub }) {
    const international = readAppLanguage() !== 'ko';
    return (
        <Box sx={{
            p: '12px', bgcolor: '#fff', ...(international && { minWidth: 0, overflowWrap: 'anywhere' }),
            border: '1px solid #f0f0f0', borderRadius: '12px',
        }}>
            <SingleLineText sx={{ fontSize: 11, fontWeight: 700, color: '#6B7280', ...(international && { whiteSpace: 'normal' }) }}>{label}</SingleLineText>
            <Typography sx={{
                fontSize: 17, fontWeight: 800, color: '#0c4a6e',
                mt: '2px', letterSpacing: '-0.01em',
                fontVariantNumeric: 'tabular-nums',
            }}>{value}</Typography>
            {sub && (
                <Typography component="div" sx={{ fontSize: 12, mt: '2px' }}>
                    {sub}
                </Typography>
            )}
        </Box>
    );
}
